import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import CommonLayout from "./CommonLayout";
import { AppDispatch, RootState } from "redux/store";
import { Alert } from "../../shared/Alert/Alert";
import SocialMediaLink from "./SocialMediaLink";
import Notifications from "./Notifications";
import DeleteAccount from "./DeleteAccount";
import AccountInfo from "./AccountInfo";
import StudentAccountInfo from "./StudentAccountInfo";
import {
  getCurrentUser,
  updateUserEmailAsVerified,
  resendVerificationEmail, // ADD THIS IMPORT
} from "../../redux/reducers/UserSlice/UserSlice";
import { useEffect, useState } from "react";
import successHandler from "../../utils/helpers/SuccessHandler";
import { toast } from "react-toastify";
import InfoHandler from "utils/helpers/InfoHandler";
import { useLocation } from "react-router-dom";
import SEO from "components/SEO/SEO";
import { captureError } from "utils/sentry";

const AccountPage = ({ className = "" }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [localCount, setLocalCount] = useState(0);
  
  // ADD THESE TWO NEW STATE VARIABLES
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const dispatch = useDispatch<AppDispatch>();
  const { authenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  useEffect(() => {
    if (authenticated && currentUser) {
      if (!currentUser?.email_verified) {
        toast.dismiss();
        InfoHandler(
          "Please verify your email before trying to access any features in our application."
        );
      } else if (currentUser?.is_host && !currentUser?.host_verified) {
        toast.dismiss();
        InfoHandler(
          "Your profile has not been approved, please wait until the process is completed to access features on our platform."
        );
      } else if (!currentUser?.profile_complete) {
        toast.dismiss();
        InfoHandler(
          "Please complete all account information before accessing features."
        );
      }
    }
    return () => {
      toast.dismiss();
    };
  }, [location?.pathname]);

  // ADD THIS NEW USEEFFECT FOR COOLDOWN TIMER
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (
      currentUser &&
      currentUser?.email_verified &&
      currentUser?.sign_in_count === 1 &&
      localCount === 0
    ) {
      dispatch(updateUserEmailAsVerified())
        .unwrap()
        .then((res) => {
          if (res) {
            toast.dismiss();
            successHandler("Email verified successfully!");
            dispatch(getCurrentUser());
            setLocalCount(1);
          }
        })
        .catch((error) => {
       
  captureError(error, { stage: "email_verification", userId: currentUser?.id });
        });
    }
  }, [currentUser, localCount, dispatch]);

 const handleResendVerification = async () => {
  if (isResending || resendCooldown > 0) return;
  
  setIsResending(true);
  try {
    await dispatch(resendVerificationEmail()).unwrap();
    
    toast.dismiss();
    successHandler("Verification email sent successfully! Please check your inbox.");
    setResendCooldown(60); // 60 second cooldown
  } catch (error: any) {
    toast.dismiss();
   const errorMessage = error?.message || error?.data?.message || "Failed to resend verification email. Please try again.";
toast.error(errorMessage);
captureError(error, { stage: "resend_verification_email", userId: currentUser?.id });
  } finally {
    setIsResending(false);
  }
};

  // Check if user needs to verify email (students only)
  const needsEmailVerification = currentUser && !currentUser.email_verified;

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
     <SEO
  title="Account Settings | Nomadroof"
  description="Manage your Nomadroof account settings, personal information, and preferences."
/>
      <CommonLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Account Settings
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Manage your account preferences and personal information
            </p>
          </div>

          {/* Email Verification Gate */}
          {needsEmailVerification ? (
            <div className="flex items-center justify-center min-h-[400px] sm:min-h-[500px]">
              <div className="max-w-md w-full mx-auto">
                <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-lg border border-gray-200 dark:border-neutral-800 overflow-hidden">
                  <div className="px-6 py-8 sm:px-8 sm:py-10 text-center">
                    {/* Email Icon */}
                    <div className="mx-auto flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4 sm:mb-6">
                      <svg
                        className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 dark:text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                      Verify Your Email
                    </h2>

                    {/* Description */}
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed">
                      Please confirm your email address before accessing your account
                      settings. We've sent a verification link to{" "}
                      <span className="font-semibold text-gray-900 dark:text-white break-all">
                        {currentUser?.email}
                      </span>
                    </p>

                    {/* Instructions */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 sm:p-5 mb-6 sm:mb-8">
                      <div className="flex items-start text-left">
                        <svg
                          className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
                          <p className="font-medium mb-1">What to do next:</p>
                          <ul className="space-y-1 ml-1">
                            <li>1. Check your email inbox</li>
                            <li>2. Click the verification link</li>
                            <li>3. Return here to access your account</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Help Text - UPDATED THIS SECTION */}
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      Didn't receive the email?{" "}
                      <button 
                        onClick={handleResendVerification}
                        disabled={isResending || resendCooldown > 0}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                      >
                        {isResending 
                          ? "Sending..." 
                          : resendCooldown > 0 
                            ? `Resend in ${resendCooldown}s` 
                            : "Resend verification email"}
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (

          <div className="space-y-4 sm:space-y-6">
            {/* Alert Section - Only show for verified users with pending host approval */}
            {currentUser &&
              currentUser.email_verified &&
              currentUser.is_host &&
              !currentUser.host_verified && (
                <Alert type="info">
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm sm:text-base">Profile Under Review</p>
                      <p className="text-xs sm:text-sm mt-1">
                        Once the admin approves your profile, you'll be able to list
                        your properties.
                      </p>
                    </div>
                  </div>
                </Alert>
              )}

            {/* Account Information Card - For Everyone */}
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-neutral-800">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {currentUser?.is_student ? "Student Profile" : "Account Information"}
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {currentUser?.is_student 
                        ? "Manage your student information and credentials"
                        : "Your personal account details and settings"}
                    </p>
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 self-start sm:self-auto">
                    {currentUser?.is_student ? "Student" : "Host"}
                  </span>
                </div>
              </div>
              <div className="px-4 sm:px-6 py-4 sm:py-6">
                {currentUser?.is_student ? <StudentAccountInfo /> : <AccountInfo />}
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-neutral-800">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Social Media
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Connect your social media accounts
                </p>
              </div>
              <div className="px-4 sm:px-6 py-4 sm:py-6">
                <SocialMediaLink />
              </div>
            </div>

            {/* Communication Preferences Section */}
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-neutral-800">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Communication Preferences
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Manage your notification and communication preferences
                </p>
              </div>
              <div className="px-4 sm:px-6 py-4 sm:py-6">
                <Notifications />
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-red-50 dark:bg-red-900/10 rounded-xl shadow-sm border-2 border-red-200 dark:border-red-900/50 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-red-200 dark:border-red-900/50">
                <div className="flex items-start sm:items-center">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-500 mr-2 sm:mr-3 mt-0.5 sm:mt-0 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-red-900 dark:text-red-400">
                      Danger Zone
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-red-700 dark:text-red-500">
                      Irreversible actions that affect your account
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-4 sm:px-6 py-4 sm:py-6">
                <DeleteAccount />
              </div>
            </div>
          </div>
          )}
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountPage;